import type { CaseInputs, DriverType, View } from './engine/types';
import { LOCATION_VIEWS } from './engine/types';

const TOTAL_ORDERS_KEY = 'is.total_orders';
const AVG_ORDERS_KEY = 'is.avg_orders_per_day';
const DAYS_OPEN_KEY = 'is.days_open_for_business';

const COPY_DRIVER_TYPES: DriverType[] = [
    'mom_pct',
    'mom_dollar',
    'yoy_pct',
    'yoy_dollar',
    'pct_of_line',
    's_curve',
    'carry_forward',
];

function resolveDaysOpenValues(
    viewDrivers: Record<string, CaseInputs['drivers'][View][string]>,
): (number | null)[] {
    const daysState = viewDrivers[DAYS_OPEN_KEY];
    if (!daysState?.active) {
        return Array(36).fill(null);
    }
    const cache = daysState.by_type[daysState.active];
    if (!cache?.values) {
        return Array(36).fill(null);
    }
    return cache.values.map((value) =>
        typeof value === 'number' && Number.isFinite(value) ? value : null,
    );
}

function divideTotalsByDaysOpen(
    totals: (number | null)[],
    daysOpen: (number | null)[],
): (number | null)[] {
    return totals.map((total, index) => {
        const days = daysOpen[index];
        if (typeof total !== 'number' || typeof days !== 'number' || days === 0) {
            return null;
        }
        return total / days;
    });
}

function migrateViewDrivers(viewDrivers: Record<string, CaseInputs['drivers'][View][string]>): void {
    const old = viewDrivers[TOTAL_ORDERS_KEY];
    if (!old?.active) {
        return;
    }
    if (viewDrivers[AVG_ORDERS_KEY]?.active) {
        delete viewDrivers[TOTAL_ORDERS_KEY];
        return;
    }

    const active = old.active;
    const oldCache = old.by_type[active];
    if (!oldCache) {
        delete viewDrivers[TOTAL_ORDERS_KEY];
        return;
    }

    if (active === 'count_per_open_day' || active === 'count_per_calendar_day') {
        viewDrivers[AVG_ORDERS_KEY] = {
            active: 'count_value',
            by_type: {
                count_value: {
                    values: [...(oldCache.values ?? Array(36).fill(null))],
                    params: oldCache.params,
                },
            },
        };
    } else if (active === 'count_value') {
        const totals = oldCache.values ?? Array(36).fill(null);
        const daysOpen = resolveDaysOpenValues(viewDrivers);
        const hasResolvableDays = daysOpen.some((days) => typeof days === 'number' && days !== 0);
        const avgValues = hasResolvableDays
            ? divideTotalsByDaysOpen(totals, daysOpen)
            : [...totals];
        viewDrivers[AVG_ORDERS_KEY] = {
            active: 'count_value',
            by_type: {
                count_value: {
                    values: avgValues,
                    params: oldCache.params,
                },
            },
        };
    } else if (COPY_DRIVER_TYPES.includes(active)) {
        viewDrivers[AVG_ORDERS_KEY] = {
            active,
            by_type: {
                [active]: {
                    values: [...(oldCache.values ?? Array(36).fill(null))],
                    params: oldCache.params ? { ...oldCache.params } : undefined,
                },
            },
        };
    }

    delete viewDrivers[TOTAL_ORDERS_KEY];
}

export function migrateTotalOrdersDriversToAvgOrders(inputs: CaseInputs): CaseInputs {
    const next: CaseInputs = {
        ...inputs,
        drivers: { ...inputs.drivers },
    };

    for (const view of LOCATION_VIEWS) {
        if (!inputs.drivers[view]?.[TOTAL_ORDERS_KEY]?.active) {
            continue;
        }
        const viewDrivers = { ...(next.drivers[view] ?? {}) };
        migrateViewDrivers(viewDrivers);
        if (Object.keys(viewDrivers).length > 0) {
            next.drivers[view] = viewDrivers;
        } else {
            delete next.drivers[view];
        }
    }

    return next;
}
